// Пары id блоков `[старый, новый]` при смене источника чтения курса — для
// `useReadSourceRemap`. Старый список — из кеша RTK, новый — запросом.
import type { RootState } from '@/store'
import type { useAppDispatch } from '@/store/hooks'
import type { Block } from '@/types/blocks'
import { lessonsApi } from '@/features/editor/lessonsApi'
import type { IdPair } from '@/features/editor/lib/idAliases'
import { remapId, type WithOrigin } from '@/features/moderation/lib/liveIdentity'

export type AppDispatch = ReturnType<typeof useAppDispatch>

/** Пары `[старый, новый]` для всех сущностей прежнего списка, чей id сменился. */
export const idPairs = (prev: readonly WithOrigin[], next: readonly WithOrigin[]): IdPair[] =>
  prev.map((e): IdPair => [e.id, remapId(e.id, prev, next)]).filter(([from, to]) => from !== to)

const loadBlocks = async (dispatch: AppDispatch, lessonId: number): Promise<Block[] | null> => {
  // Подписка держится до ответа: `unwrap()` читает данные из КЕША уже после запроса, а
  // отложенная инвалидация RTK (`invalidationBehavior: 'delayed'`) выкидывает
  // неподписанную запись ровно в этот момент — `unwrap()` отдал бы `undefined`, и
  // переезд молча оборвался бы, оставив выделение на id ушедшего источника.
  // `forceRefetch`: кеш этого урока мог остаться с ДО-черновиковой сессии (живой урок
  // 10 до черновика) — по нему выделение нового блока ушло бы в `null`.
  const req = dispatch(
    lessonsApi.endpoints.getLessonBlocks.initiate(lessonId, { forceRefetch: true }),
  )
  try {
    return (await req.unwrap()) ?? null
  } catch {
    return null
  } finally {
    req.unsubscribe()
  }
}

/** Уроки нового источника, которые переезд перечитает сам (`forceRefetch`): выделенный и
 *  каждый, чей старый список в кеше. Их инвалидировать незачем — под подпиской загрузки
 *  инвалидация обернулась бы вторым запросом того же урока. */
export const lessonsToLoad = (
  getState: () => RootState,
  lessons: IdPair[],
  selectedLessonId: number | null,
): Set<number> => {
  const ids = new Set<number>()
  // Выделенный урок без пары (id не сменился) переезд перечитывает под тем же id.
  if (selectedLessonId !== null && !lessons.some(([from]) => from === selectedLessonId))
    ids.add(selectedLessonId)
  for (const [from, to] of lessons) {
    if (to === null) continue
    const cached = lessonsApi.endpoints.getLessonBlocks.select(from)(getState()).data
    if (from === selectedLessonId || cached) ids.add(to)
  }
  return ids
}

/**
 * Пары блоков выделенного урока: старый список — из кеша, новый — запросом. Выделенный
 * блок, которому некуда переехать, получает пару `[id, null]` — выделение сбрасывается.
 */
export const blockPairs = async (
  getState: () => RootState,
  dispatch: AppDispatch,
  oldLessonId: number,
  newLessonId: number | null,
): Promise<IdPair[]> => {
  const oldBlocks = lessonsApi.endpoints.getLessonBlocks.select(oldLessonId)(getState()).data ?? []
  const { selectedBlockId, selectedBlockIds } = getState().editor
  const selected = [...new Set([selectedBlockId, ...selectedBlockIds])].filter(
    (id): id is number => id !== null,
  )
  const newBlocks = newLessonId === null ? null : await loadBlocks(dispatch, newLessonId)
  // Нового списка нет (урок исчез или не загрузился) — выделенным блокам некуда переехать.
  if (newBlocks === null) return selected.map((id): IdPair => [id, null])
  const pairs = idPairs(oldBlocks, newBlocks)
  const unmapped = selected.filter((id) => !newBlocks.some((b) => b.id === id))
  const missing = unmapped.filter((id) => !pairs.some(([from]) => from === id))
  return [...pairs, ...missing.map((id): IdPair => [id, null])]
}

/**
 * Пары блоков невыделенных уроков, чей старый список в кеше: цель загрузки или прогона
 * бывает только в посещённом уроке (список грузила панель или `useEditScope`). Без них
 * загрузка в таком уроке упёрлась бы в `GONE_MESSAGE` и удалила бы файл, а прогон правки
 * потерял бы свои блоки. Некешированные уроки не запрашиваются — переезд не должен стоить
 * запроса на каждый урок курса; пар `[id, null]` нет — невыделенного нечего сбрасывать.
 */
export const cachedLessonsBlockPairs = async (
  getState: () => RootState,
  dispatch: AppDispatch,
  lessons: IdPair[],
  skipLessonId: number | null,
): Promise<IdPair[]> => {
  const jobs = lessons.map(async ([from, to]) => {
    if (from === skipLessonId || to === null) return []
    const old = lessonsApi.endpoints.getLessonBlocks.select(from)(getState()).data
    if (!old) return []
    const fresh = await loadBlocks(dispatch, to)
    return fresh ? idPairs(old, fresh) : []
  })
  return (await Promise.all(jobs)).flat()
}
